import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { CONTENT_PAGE_HEADERS } from "~/lib/cache.server"
import { useLoaderData, Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { FAQSchema, BreadcrumbSchema } from '~/components/structured-data'
import { ArrowRight, CheckCircle, XCircle, Scale, Zap, ThumbsUp } from 'lucide-react'

// ─── Comparison data ─────────────────────────────────────────

interface ComparisonData {
  title: string
  seoTitle: string
  description: string
  optionA: { name: string; emoji: string }
  optionB: { name: string; emoji: string }
  intro: string
  criteria: Array<{
    label: string
    a: string
    b: string
    winner: 'a' | 'b' | 'tie'
  }>
  prosA: string[]
  consA: string[]
  prosB: string[]
  consB: string[]
  verdict: string
  faqs: Array<{ question: string; answer: string }>
  relatedSlugs: string[]
}

const COMPARISONS: Record<string, ComparisonData> = {
  'chaudiere-gaz-vs-pompe-a-chaleur': {
    title: 'Chaudière gaz vs Pompe à chaleur : que choisir en 2026 ?',
    seoTitle: 'Chaudière gaz vs Pompe à chaleur : comparatif complet 2026 | AfficheServices',
    description: 'Comparaison détaillée chaudière gaz condensation vs PAC air-eau : prix, consommation, aides, confort et impact environnemental.',
    optionA: { name: 'Chaudière gaz condensation', emoji: '🔥' },
    optionB: { name: 'Pompe à chaleur air-eau', emoji: '♻️' },
    intro: 'Le remplacement de votre système de chauffage est un investissement important. Chaudière gaz condensation ou pompe à chaleur air-eau ? Voici notre comparatif objectif pour vous aider à choisir.',
    criteria: [
      { label: 'Prix d\'installation', a: '3 000–6 000 €', b: '8 000–16 000 €', winner: 'a' },
      { label: 'Coût annuel (120 m²)', a: '1 400–1 800 €', b: '900–1 200 €', winner: 'b' },
      { label: 'Aides financières', a: 'Aucune (fin en 2025)', b: 'Jusqu\'à 9 000 €', winner: 'b' },
      { label: 'Reste à charge', a: '3 000–6 000 €', b: '4 000–10 000 €', winner: 'tie' },
      { label: 'Durée de vie', a: '15–20 ans', b: '15–20 ans', winner: 'tie' },
      { label: 'Impact écologique', a: 'Énergie fossile (CO2)', b: 'Énergie renouvelable', winner: 'b' },
      { label: 'Bruit', a: 'Silencieux', b: '45-55 dB (unité ext.)', winner: 'a' },
      { label: 'Performance grand froid', a: 'Stable', b: 'Réduite < -7°C', winner: 'a' },
    ],
    prosA: ['Installation moins chère', 'Montée en température rapide', 'Silencieux', 'Fonctionne par tous les temps'],
    consA: ['Énergie fossile (CO2)', 'Prix du gaz en hausse', 'Plus aucune aide depuis 2025', 'Interdiction neuf depuis 2025'],
    prosB: ['Économies de 40-60%', 'Aides financières importantes', 'Énergie renouvelable', 'Chauffage + eau chaude'],
    consB: ['Installation plus chère', 'Bruit de l\'unité extérieure', 'Performance réduite par grand froid', 'ROI sur 5-8 ans'],
    verdict: 'En 2026, la pompe à chaleur air-eau est le choix le plus pertinent pour la majorité des foyers. Avec les aides, le reste à charge est comparable à une chaudière gaz, mais les économies annuelles sont nettement supérieures.',
    faqs: [
      { question: 'Peut-on remplacer une chaudière gaz par une PAC sans tout changer ?', answer: 'Oui, la PAC air-eau se raccorde sur vos radiateurs existants. Seul le générateur change.' },
      { question: 'La PAC fonctionne-t-elle quand il fait très froid ?', answer: 'Oui, mais son rendement baisse en dessous de -7°C. Les modèles récents fonctionnent jusqu\'à -25°C.' },
      { question: 'La chaudière gaz sera-t-elle interdite ?', answer: 'En neuf oui (depuis 2025). En rénovation, elle reste autorisée mais n\'est plus subventionnée.' },
    ],
    relatedSlugs: ['ite-vs-iti-isolation', 'radiateur-electrique-vs-plancher-chauffant', 'chaudiere-granules-vs-pompe-a-chaleur'],
  },

  'ite-vs-iti-isolation': {
    title: 'ITE vs ITI : quelle isolation choisir pour votre maison ?',
    seoTitle: 'ITE vs ITI : comparatif isolation extérieure vs intérieure | AfficheServices',
    description: 'Isolation par l\'Extérieur (ITE) ou par l\'Intérieur (ITI) : prix, performance, avantages et inconvénients.',
    optionA: { name: 'ITE (Isolation par l\'Extérieur)', emoji: '🏠' },
    optionB: { name: 'ITI (Isolation par l\'Intérieur)', emoji: '🧱' },
    intro: 'L\'isolation des murs représente 20-25% des déperditions thermiques. Deux techniques : ITE et ITI. Laquelle choisir ?',
    criteria: [
      { label: 'Prix au m²', a: '100–200 €', b: '40–80 €', winner: 'b' },
      { label: 'Performance thermique', a: 'Excellente', b: 'Bonne', winner: 'a' },
      { label: 'Surface habitable', a: 'Préservée', b: 'Réduite (-5 à -7%)', winner: 'a' },
      { label: 'Aides MaPrimeRénov\'', a: 'Jusqu\'à 75 €/m²', b: 'Jusqu\'à 25 €/m²', winner: 'a' },
      { label: 'Perturbation travaux', a: 'Aucune (extérieur)', b: 'Forte (pièce par pièce)', winner: 'a' },
      { label: 'Aspect extérieur', a: 'Modifié', b: 'Inchangé', winner: 'b' },
      { label: 'Copropriété', a: 'Vote en AG', b: 'Libre', winner: 'b' },
    ],
    prosA: ['Supprime les ponts thermiques', 'Préserve la surface habitable', 'Pas de travaux intérieurs', 'Ravalement + isolation en un chantier'],
    consA: ['2 à 3 fois plus cher', 'Modifie l\'aspect extérieur', 'Échafaudage nécessaire', 'Vote copropriété'],
    prosB: ['Moins cher', 'Pas d\'autorisation extérieure', 'Pièce par pièce possible', 'Rapide'],
    consB: ['Réduit la surface habitable', 'Ponts thermiques résiduels', 'Perturbation pendant travaux', 'Risque de condensation'],
    verdict: 'L\'ITE est objectivement la meilleure solution thermique. L\'ITI reste pertinente si le budget est limité ou si l\'aspect extérieur ne peut pas être modifié.',
    faqs: [
      { question: 'Peut-on combiner ITE et ITI ?', answer: 'Oui, ITE sur les grandes façades et ITI sur les murs mitoyens ou inaccessibles.' },
      { question: 'L\'ITE est-elle possible en copropriété ?', answer: 'Oui, avec un vote en AG. Les aides MaPrimeRénov\' Copro peuvent aider.' },
    ],
    relatedSlugs: ['chaudiere-gaz-vs-pompe-a-chaleur', 'simple-vitrage-vs-double-vitrage', 'laine-de-verre-vs-laine-de-roche'],
  },

  'simple-vitrage-vs-double-vitrage': {
    title: 'Simple vitrage vs Double vitrage : prix et économies',
    seoTitle: 'Simple vs Double vitrage : comparatif prix et performance | AfficheServices',
    description: 'Faut-il passer au double vitrage ? Performances, prix et aides financières.',
    optionA: { name: 'Simple vitrage', emoji: '🪟' },
    optionB: { name: 'Double vitrage', emoji: '🪟' },
    intro: 'Les fenêtres en simple vitrage causent 10 à 15% des pertes de chaleur. Le passage au double vitrage est l\'un des gestes les plus efficaces.',
    criteria: [
      { label: 'Isolation thermique', a: '5-6 W/m².K', b: '1,1-1,4 W/m².K', winner: 'b' },
      { label: 'Isolation phonique', a: '20-25 dB', b: '30-40 dB', winner: 'b' },
      { label: 'Prix par fenêtre', a: 'Existant', b: '300–800 €', winner: 'a' },
      { label: 'Économies chauffage', a: 'Référence', b: '-10 à -15%', winner: 'b' },
      { label: 'Condensation', a: 'Fréquente', b: 'Rare', winner: 'b' },
      { label: 'Sécurité', a: 'Fragile', b: 'Plus résistant', winner: 'b' },
    ],
    prosA: ['Pas de travaux', 'Charme de l\'ancien'],
    consA: ['Très mauvaise isolation', 'Condensation', 'Bruit', 'Facture élevée'],
    prosB: ['Économies 10-15%', 'Confort thermique et acoustique', 'Moins de condensation', 'Aides disponibles'],
    consB: ['Investissement initial', 'Travaux de pose'],
    verdict: 'Le passage au double vitrage est toujours rentable. Pour 10 fenêtres, l\'investissement de 5 000–8 000 € est amorti en 5-8 ans. Le triple vitrage ne vaut que pour les façades nord en zone froide.',
    faqs: [
      { question: 'Le triple vitrage vaut-il le coup ?', answer: 'Rarement en rénovation. Le surcoût de 30-50% n\'est rentable que pour les façades nord en zone H1.' },
    ],
    relatedSlugs: ['ite-vs-iti-isolation', 'chaudiere-gaz-vs-pompe-a-chaleur'],
  },

  'radiateur-electrique-vs-plancher-chauffant': {
    title: 'Radiateur électrique vs Plancher chauffant : que choisir ?',
    seoTitle: 'Radiateur électrique vs Plancher chauffant : comparatif | AfficheServices',
    description: 'Radiateurs ou plancher chauffant : confort, prix, consommation et quel choix selon votre situation.',
    optionA: { name: 'Radiateurs électriques (inertie)', emoji: '🔌' },
    optionB: { name: 'Plancher chauffant', emoji: '🏠' },
    intro: 'Le choix entre radiateurs et plancher chauffant impacte votre confort, votre facture et la valeur de votre bien.',
    criteria: [
      { label: 'Prix installation', a: '300–800 €/radiateur', b: '50–120 €/m²', winner: 'a' },
      { label: 'Confort', a: 'Bon (inertie)', b: 'Excellent (uniforme)', winner: 'b' },
      { label: 'Consommation', a: 'Moyenne à haute', b: 'Basse', winner: 'b' },
      { label: 'Espace', a: 'Encombrement mural', b: 'Invisible', winner: 'b' },
      { label: 'Réactivité', a: 'Rapide (30 min)', b: 'Lente (2-4h)', winner: 'a' },
      { label: 'Travaux', a: 'Simples', b: 'Lourds (chape)', winner: 'a' },
      { label: 'Compatible PAC', a: 'Non', b: 'Oui', winner: 'b' },
    ],
    prosA: ['Installation simple', 'Prix accessible', 'Réglage par pièce', 'Montée rapide'],
    consA: ['Consommation élevée', 'Encombre les murs', 'Chaleur inégale'],
    prosB: ['Confort optimal', 'Invisible', 'Basse consommation avec PAC', 'Valorise le bien'],
    consB: ['Travaux lourds', 'Coût élevé', 'Réactivité lente', 'Réparation difficile'],
    verdict: 'Le plancher chauffant est idéal en construction neuve ou rénovation lourde. Les radiateurs à inertie sont le choix pragmatique en rénovation légère.',
    faqs: [
      { question: 'Plancher chauffant compatible avec du parquet ?', answer: 'Oui, avec du contrecollé ou stratifié (pas de massif > 15 mm).' },
    ],
    relatedSlugs: ['chaudiere-gaz-vs-pompe-a-chaleur', 'ite-vs-iti-isolation'],
  },

  'laine-de-verre-vs-laine-de-roche': {
    title: 'Laine de verre vs Laine de roche : quel isolant choisir ?',
    seoTitle: 'Laine de verre vs Laine de roche : comparatif isolants | AfficheServices',
    description: 'Comparaison laine de verre et laine de roche : performance, prix et applications.',
    optionA: { name: 'Laine de verre', emoji: '🟡' },
    optionB: { name: 'Laine de roche', emoji: '⚫' },
    intro: 'Les deux isolants les plus utilisés en France. Très proches en performance, avec des avantages spécifiques chacun.',
    criteria: [
      { label: 'Conductivité thermique', a: '0,030–0,040', b: '0,034–0,045', winner: 'a' },
      { label: 'Isolation phonique', a: 'Bonne', b: 'Très bonne', winner: 'b' },
      { label: 'Prix (R=7)', a: '8–15 €/m²', b: '12–20 €/m²', winner: 'a' },
      { label: 'Résistance humidité', a: 'Moyenne', b: 'Bonne', winner: 'b' },
      { label: 'Confort été', a: 'Moyen', b: 'Meilleur', winner: 'b' },
      { label: 'Tassement', a: 'Possible', b: 'Très faible', winner: 'b' },
    ],
    prosA: ['Moins chère', 'Très légère', 'Excellent rapport qualité/prix'],
    consA: ['Sensible à l\'humidité', 'Confort été inférieur', 'Peut se tasser'],
    prosB: ['Meilleure isolation phonique', 'Meilleur confort été', 'Résiste à l\'humidité', 'Ne se tasse pas'],
    consB: ['Plus chère (20-30%)', 'Plus lourde', 'Plus rigide'],
    verdict: 'Laine de verre pour les combles perdus et murs intérieurs (meilleur rapport qualité/prix). Laine de roche pour les murs humides, l\'isolation phonique et les toitures (meilleur confort été).',
    faqs: [
      { question: 'La ouate de cellulose est-elle meilleure ?', answer: 'Meilleur confort été mais 20-40% plus chère. Bon choix écologique pour les combles en soufflage.' },
    ],
    relatedSlugs: ['ite-vs-iti-isolation', 'simple-vitrage-vs-double-vitrage'],
  },

  'chaudiere-granules-vs-pompe-a-chaleur': {
    title: 'Chaudière granulés vs Pompe à chaleur : comparatif 2026',
    seoTitle: 'Chaudière granulés vs PAC : quel chauffage choisir ? | AfficheServices',
    description: 'Chaudière à granulés vs pompe à chaleur : prix, aides, consommation et critères de choix.',
    optionA: { name: 'Chaudière à granulés', emoji: '🪵' },
    optionB: { name: 'PAC air-eau', emoji: '♻️' },
    intro: 'Deux solutions écologiques se disputent la première place. Les deux sont éligibles aux aides, mais le meilleur choix dépend de votre situation.',
    criteria: [
      { label: 'Prix installation', a: '12 000–20 000 €', b: '8 000–16 000 €', winner: 'b' },
      { label: 'Coût annuel', a: '1 000–1 400 €', b: '900–1 200 €', winner: 'b' },
      { label: 'Aides max', a: 'Jusqu\'à 14 000 €', b: 'Jusqu\'à 9 000 €', winner: 'a' },
      { label: 'Encombrement', a: 'Important (+ silo)', b: 'Modéré', winner: 'b' },
      { label: 'Entretien', a: 'Fréquent', b: 'Simple (annuel)', winner: 'b' },
      { label: 'Grand froid', a: 'Stable', b: 'Réduit < -7°C', winner: 'a' },
      { label: 'Climatisation', a: 'Non', b: 'Oui (réversible)', winner: 'b' },
    ],
    prosA: ['Aides plus élevées', 'Tous les temps', 'Combustible local', 'Confort gaz'],
    consA: ['Très encombrant', 'Entretien fréquent', 'Prix granulés variable'],
    prosB: ['Moins cher', 'Entretien simple', 'Compact', 'Climatisation été'],
    consB: ['Grand froid', 'Bruit extérieur', 'Aides inférieures'],
    verdict: 'PAC air-eau dans la majorité des cas. Chaudière granulés si vous êtes en zone froide ou si vous préférez un combustible local.',
    faqs: [
      { question: 'Peut-on coupler les deux ?', answer: 'Oui, mais rarement rentable. La PAC couvre 80% des besoins.' },
    ],
    relatedSlugs: ['chaudiere-gaz-vs-pompe-a-chaleur', 'radiateur-electrique-vs-plancher-chauffant'],
  },

  'peinture-acrylique-vs-glycero': {
    title: 'Peinture acrylique vs Glycéro : laquelle choisir ?',
    seoTitle: 'Peinture acrylique vs Glycéro : comparatif | AfficheServices',
    description: 'Acrylique ou glycéro ? Rendu, durabilité, odeur, séchage et applications.',
    optionA: { name: 'Acrylique (à l\'eau)', emoji: '💧' },
    optionB: { name: 'Glycéro (à l\'huile)', emoji: '🎨' },
    intro: 'Le choix impacte le rendu, la durabilité et votre santé. La tendance est à l\'acrylique, mais la glycéro garde des avantages.',
    criteria: [
      { label: 'Odeur', a: 'Très faible', b: 'Forte', winner: 'a' },
      { label: 'Séchage', a: '1-2 heures', b: '6-8 heures', winner: 'a' },
      { label: 'COV', a: 'Très faibles', b: 'Élevés', winner: 'a' },
      { label: 'Rendu laqué', a: 'Bon', b: 'Excellent', winner: 'b' },
      { label: 'Résistance humidité', a: 'Bonne', b: 'Excellente', winner: 'b' },
      { label: 'Jaunissement', a: 'Non', b: 'Oui', winner: 'a' },
      { label: 'Prix', a: '15–35 €/L', b: '20–40 €/L', winner: 'a' },
    ],
    prosA: ['Pas d\'odeur', 'Séchage rapide', 'Écologique', 'Ne jaunit pas'],
    consA: ['Laqué moins parfait', 'Moins résistante humidité'],
    prosB: ['Rendu laqué impeccable', 'Très résistante humidité', 'Lessivable'],
    consB: ['Odeur forte', 'COV nocifs', 'Séchage long', 'Jaunit'],
    verdict: 'Acrylique dans 90% des cas. Glycéro uniquement pour les boiseries laquées haut de gamme et les pièces très humides.',
    faqs: [
      { question: 'Acrylique sur glycéro, c\'est possible ?', answer: 'Pas directement. Il faut poncer et appliquer un primaire d\'accrochage.' },
    ],
    relatedSlugs: ['laine-de-verre-vs-laine-de-roche'],
  },

  'parquet-massif-vs-stratifie': {
    title: 'Parquet massif vs Stratifié : prix, durée de vie et entretien',
    seoTitle: 'Parquet massif vs Stratifié : comparatif complet | AfficheServices',
    description: 'Parquet massif ou stratifié ? Prix, durée de vie, entretien et rendu comparés.',
    optionA: { name: 'Parquet massif', emoji: '🪵' },
    optionB: { name: 'Sol stratifié', emoji: '📋' },
    intro: 'Visuellement similaires mais radicalement différents en qualité, prix et durée de vie.',
    criteria: [
      { label: 'Prix fourni-posé', a: '60–150 €/m²', b: '15–40 €/m²', winner: 'b' },
      { label: 'Durée de vie', a: '50–100 ans', b: '10–20 ans', winner: 'a' },
      { label: 'Ponçage possible', a: 'Oui (5-8 fois)', b: 'Non', winner: 'a' },
      { label: 'Pose', a: 'Artisan (collée/clouée)', b: 'DIY possible (clipsable)', winner: 'b' },
      { label: 'Acoustique', a: 'Bon', b: 'Moyen (creux)', winner: 'a' },
      { label: 'Valeur immobilière', a: 'Forte plus-value', b: 'Aucune', winner: 'a' },
    ],
    prosA: ['Dure toute une vie', 'Se rénove', 'Noble', 'Plus-value', 'Confort'],
    consA: ['Prix élevé', 'Pose pro', 'Entretien', 'Sensible eau'],
    prosB: ['Très abordable', 'Pose facile', 'Large choix décors', 'Remplacement facile'],
    consB: ['Non rénovable', 'Durée limitée', 'Bruit creux', 'Aucune plus-value'],
    verdict: 'Parquet massif pour le long terme. Stratifié pour les budgets serrés ou les locations. Le parquet contrecollé est un excellent compromis.',
    faqs: [
      { question: 'Et le parquet contrecollé ?', answer: 'Compromis idéal : aspect massif, pose simple, 30-80 €/m². Se ponce 1-2 fois.' },
    ],
    relatedSlugs: ['peinture-acrylique-vs-glycero'],
  },
}

// ─── Loader / Meta ───────────────────────────────────────────

export async function loader({ params }: LoaderFunctionArgs) {
  const { slug } = params
  const data = COMPARISONS[slug ?? '']
  if (!data) throw new Response('Comparatif non trouvé', { status: 404 })

  const relatedComparisons = data.relatedSlugs
    .filter((s) => COMPARISONS[s])
    .map((s) => ({ slug: s, title: COMPARISONS[s].title }))

  return { ...data, slug: slug ?? '', relatedComparisons }
}

export function headers() { return CONTENT_PAGE_HEADERS }

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: data?.seoTitle ?? 'Comparatif travaux' },
  { name: 'description', content: data?.description },
  { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/comparatif/${data?.slug}` },
]

// ─── Component ───────────────────────────────────────────────

function VerdictText({ text }: { text: string }) {
  const parts = text.split(/(\*\*.*?\*\*)/g)
  return (
    <p className="mt-3 text-sm leading-relaxed text-indigo-800">
      {parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**')
          ? <strong key={i}>{part.slice(2, -2)}</strong>
          : part
      )}
    </p>
  )
}

export default function ComparatifPage() {
  const { title, intro, optionA, optionB, criteria, prosA, consA, prosB, consB, verdict, faqs, slug, relatedComparisons } = useLoaderData<typeof loader>()

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <FAQSchema items={faqs} />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Comparatifs', url: 'https://www.afficheservices.fr/comparatif' },
        { name: title, url: `https://www.afficheservices.fr/comparatif/${slug}` },
      ]} />

      <div className="mx-auto max-w-4xl px-4 py-10 lg:px-6">

        {/* Hero */}
        <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 p-8 text-white">
          <div className="flex items-center gap-2 text-sm font-medium text-indigo-200">
            <Scale className="h-4 w-4" /> Comparatif objectif
          </div>
          <h1 className="mt-3 text-2xl font-bold sm:text-3xl">{title}</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-indigo-200">{intro}</p>
        </div>

        {/* Comparison table */}
        <div className="mt-8 overflow-hidden rounded-2xl border border-border bg-white shadow-xs">
          <div className="grid grid-cols-3 border-b border-border bg-slate-50 text-center">
            <div className="border-r border-border p-3 text-xs font-semibold text-slate-500">Critère</div>
            <div className="border-r border-border p-3 text-xs font-bold text-indigo-700">{optionA.emoji} {optionA.name}</div>
            <div className="p-3 text-xs font-bold text-purple-700">{optionB.emoji} {optionB.name}</div>
          </div>
          {criteria.map((c, i) => (
            <div key={i} className={`grid grid-cols-3 border-b border-border last:border-0 ${i % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
              <div className="border-r border-border p-3 text-xs font-medium text-slate-700">{c.label}</div>
              <div className={`border-r border-border p-3 text-center text-xs ${c.winner === 'a' ? 'bg-emerald-50 font-semibold text-emerald-700' : 'text-slate-600'}`}>
                {c.a}
                {c.winner === 'a' && <ThumbsUp className="ml-1 inline h-3 w-3" />}
              </div>
              <div className={`p-3 text-center text-xs ${c.winner === 'b' ? 'bg-emerald-50 font-semibold text-emerald-700' : 'text-slate-600'}`}>
                {c.b}
                {c.winner === 'b' && <ThumbsUp className="ml-1 inline h-3 w-3" />}
              </div>
            </div>
          ))}
        </div>

        {/* Pros and cons */}
        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          <div className="rounded-2xl border border-border bg-white p-5 shadow-xs">
            <h2 className="text-base font-bold text-indigo-700">{optionA.emoji} {optionA.name}</h2>
            <div className="mt-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500">Avantages</p>
              <ul className="mt-2 space-y-1.5">
                {prosA.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                    <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" /> {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-red-400">Inconvénients</p>
              <ul className="mt-2 space-y-1.5">
                {consA.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                    <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-white p-5 shadow-xs">
            <h2 className="text-base font-bold text-purple-700">{optionB.emoji} {optionB.name}</h2>
            <div className="mt-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500">Avantages</p>
              <ul className="mt-2 space-y-1.5">
                {prosB.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                    <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" /> {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-red-400">Inconvénients</p>
              <ul className="mt-2 space-y-1.5">
                {consB.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                    <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Verdict */}
        <div className="mt-8 rounded-2xl border-2 border-indigo-200 bg-indigo-50 p-6">
          <h2 className="flex items-center gap-2 text-base font-bold text-indigo-900">
            <Zap className="h-5 w-5 text-indigo-600" /> Notre verdict
          </h2>
          <VerdictText text={verdict} />
        </div>

        {/* FAQ */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Questions fréquentes</h2>
          <div className="mt-4 space-y-4">
            {faqs.map((faq, i) => (
              <details key={i} className="group border-b border-border pb-4 last:border-0">
                <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-800 hover:text-primary">
                  {faq.question}
                  <span className="text-slate-300 transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>

        {/* Related */}
        {relatedComparisons.length > 0 && (
          <div className="mt-8">
            <h3 className="text-sm font-bold text-slate-900">Comparatifs similaires</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {relatedComparisons.map((rc) => (
                <Link key={rc.slug} to={`/comparatif/${rc.slug}`}
                  className="rounded-full border border-border bg-white px-3 py-1.5 text-xs text-slate-600 hover:border-indigo-300 hover:text-indigo-600">
                  {rc.title}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-8 rounded-2xl bg-slate-900 p-8 text-center">
          <h2 className="text-xl font-bold text-white">Besoin d'un devis ?</h2>
          <p className="mt-2 text-sm text-slate-400">3 devis gratuits d'artisans vérifiés sous 48h</p>
          <Link to="/demande-devis/1" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3 text-sm font-semibold text-white shadow-md hover:bg-primary-hover btn-press">
            Demander un devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <Footer contextTitle="Autres comparatifs" contextLinks={
        relatedComparisons.map((rc) => ({ href: `/comparatif/${rc.slug}`, label: rc.title }))
      } />
    </div>
  )
}
